import type { FunctionComponent } from "preact";
import { useEffect, useId, useMemo, useState } from "preact/hooks";

import { fetchRegions } from "./api.js";
import { ListView, useEmbedActions } from "./ListView.js";
import { useT } from "./messages.js";
import { MapView } from "./MapView.js";
import { isSubRegion, UMLAND_SLUG, type EmbedConfig, type PublicRegion } from "./types.js";

// Map + list sharing a single fetch (useEmbedActions), no filter UI. Used both
// for org-scoped embeds and the district-fixed (data-cleanups-region) mode. The
// map plots the full upcoming set; the list slices it.
export const ScopedEmbed: FunctionComponent<{ config: EmbedConfig; region?: string }> = ({ config, region }) => {
  const data = useEmbedActions(config, region);
  return (
    <>
      {config.showMap && <MapView config={config} org={data.org} actions={data.upcoming.actions} />}
      <ListView config={config} data={data} />
    </>
  );
};

export const BerlinWideEmbed: FunctionComponent<{ config: EmbedConfig }> = ({ config }) => {
  // District-scoped mode (data-cleanups-region): the region is fixed by the
  // host page, so the filter UI is omitted and the region passes straight
  // through to the list/map.
  if (config.region) {
    return <ScopedEmbed config={config} region={config.region} />;
  }
  return <BerlinWideFilteredEmbed config={config} />;
};

const BerlinWideFilteredEmbed: FunctionComponent<{ config: EmbedConfig }> = ({ config }) => {
  const t = useT();
  const [allRegions, setAllRegions] = useState<PublicRegion[]>([]);
  const [selectedDistrict, setSelectedDistrict] = useState<string | undefined>(undefined);
  const [selectedSubdistrict, setSelectedSubdistrict] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchRegions(config.apiBase)
      .then(setAllRegions)
      .catch(() => { /* filter stays hidden on error */ });
  }, [config.apiBase]);

  const districts = useMemo(
    () => allRegions.filter((r) => r.level === "district"),
    [allRegions],
  );

  // Ortsteile under a Bezirk, Gemeinden under the "Umland" grouping.
  const subdistricts = useMemo(
    () => selectedDistrict
      ? allRegions.filter((r) => isSubRegion(r.level) && r.parent_slug === selectedDistrict)
      : [],
    [allRegions, selectedDistrict],
  );

  function selectDistrict(slug: string | undefined) {
    setSelectedDistrict(slug);
    setSelectedSubdistrict(undefined);
  }

  const activeRegion = selectedSubdistrict ?? selectedDistrict;
  const data = useEmbedActions(config, activeRegion);

  const filter = (
    <>
      {districts.length > 0 && (
        <RegionFilter
          regions={districts}
          selected={selectedDistrict}
          onSelect={selectDistrict}
          label={t("filterByDistrict")}
        />
      )}
      {subdistricts.length > 0 && (
        <RegionFilter
          regions={subdistricts}
          selected={selectedSubdistrict}
          onSelect={setSelectedSubdistrict}
          label={selectedDistrict === UMLAND_SLUG
            ? t("filterByMunicipality")
            : t("filterBySubdistrict")}
        />
      )}
    </>
  );

  // The filter drives both the map and the list; "below" places it between the
  // two so it can flow with a host layout that stacks content under the map.
  const filterBelow = config.filterPosition === "below";
  return (
    <>
      {!filterBelow && filter}
      {config.showMap && <MapView config={config} org={data.org} actions={data.upcoming.actions} />}
      {filterBelow && filter}
      <ListView config={config} data={data} resetKey={activeRegion ?? ""} />
    </>
  );
};

const RegionFilter: FunctionComponent<{
  regions: PublicRegion[];
  selected: string | undefined;
  onSelect: (slug: string | undefined) => void;
  label: string;
}> = ({ regions, selected, onSelect, label }) => {
  const t = useT();
  const labelId = useId();
  return (
  <div class="cb-region-filter-group">
    <span class="cb-region-filter-label" id={labelId}>{label}</span>
    <div class="cb-region-filter" role="group" aria-labelledby={labelId}>
      <button
        type="button"
        class={`cb-region-chip${!selected ? " active" : ""}`}
        onClick={() => onSelect(undefined)}
      >
        {t("filterAll")}
      </button>
      {regions.map((r) => (
        <button
          key={r.slug}
          type="button"
          class={`cb-region-chip${selected === r.slug ? " active" : ""}`}
          onClick={() => onSelect(selected === r.slug ? undefined : r.slug)}
        >
          {r.name}
        </button>
      ))}
    </div>
  </div>
  );
};
